import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import {
  PasswordResetError,
  resetPassword,
} from '@backrs_ui/common-ui/src/molecules/Login/login.util';
import { ResetPassword } from '@backrs_ui/common-ui/src/molecules/ResetPassword/ResetPassword';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { showSnackbar } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';

import { Box, Theme } from '@mui/material';
import { SxProps } from '@mui/system';

const RESET_STRINGS = commonAppStrings.modules.login.resetPassword;

const UNAUTHORIZED = 401;

const getResetErrorStatus = (error: unknown): number | undefined =>
  typeof (error as PasswordResetError | undefined)?.status === 'number'
    ? (error as PasswordResetError).status
    : undefined;

export const ResetPasswordPageContainer = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);

  const token = searchParams.get('token');

  // A reset is only possible with the token from the email link.
  useEffect(() => {
    if (!token) {
      navigate(APP_ROUTE.LOGIN, { replace: true });
    }
  }, [token, navigate]);

  const goToLogin = () => navigate(APP_ROUTE.LOGIN, { replace: true });

  const handleSubmit = async (password: string, confirmPassword: string) => {
    if (!token) {
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(password, confirmPassword, token, ENVIRONMENT_URL);
      goToLogin();
    } catch (error) {
      console.error('Failed to reset password:', error);
      const isExpiredLink = getResetErrorStatus(error) === UNAUTHORIZED;

      dispatch(
        showSnackbar({
          message: isExpiredLink ? RESET_STRINGS.error.expiredLink : RESET_STRINGS.error.tryAgain,
          severity: 'error',
        }),
      );
      setIsLoading(false);
    }
  };

  return (
    <Box sx={pageSx}>
      <ResetPassword onSubmit={handleSubmit} onBackToLogin={goToLogin} isLoading={isLoading} />
    </Box>
  );
};

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100dvh',
  width: '100%',
  backgroundColor: backrs.colors['Beige/50'],
  px: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-6xl'],
  boxSizing: 'border-box',
});
